import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap"
import { createSpecialMarketCall, updateSpecialMarketCall } from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"
import { showMessage } from "components/Common/Toaster"
import SpecialMarketLeaguesPanel from "./SpecialMarketLeaguesPanel"

const EMPTY_FORM = {
  name: "",
  market_reference_id: "",
  calculation_type: "percentage",
  calculation_value: "",
  description: "",
  settlement_condition: "",
  status: "active",
}

export default function SpecialMarketModal({
  isOpen,
  setIsOpen,
  specialMarket,
  onSaved,
}) {
  const [view, setView] = useState("form")
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [leaguesCount, setLeaguesCount] = useState(0)

  const isEditing = !!specialMarket?.id

  useEffect(() => {
    if (isOpen) {
      setView("form")
      setForm(
        specialMarket
          ? {
              name: specialMarket.name || "",
              market_reference_id: specialMarket.market_reference_id || "",
              calculation_type: specialMarket.calculation_type || "percentage",
              calculation_value: specialMarket.calculation_value ?? "",
              description: specialMarket.description || "",
              settlement_condition: specialMarket.settlement_condition || "",
              status: specialMarket.status || "active",
            }
          : EMPTY_FORM
      )
      setLeaguesCount(specialMarket?.active_leagues?.length ?? 0)
    }
  }, [isOpen, specialMarket])

  const toggle = () => setIsOpen(false)

  const buildPayload = () => {
    const payload = {
      name: form.name,
      market_reference_id: form.market_reference_id,
      calculation_type: form.calculation_type,
      calculation_value: Number(form.calculation_value),
      description: form.description,
      settlement_condition: form.settlement_condition,
    }
    if (isEditing) {
      payload.status = form.status
    }
    return payload
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = buildPayload()
      const response = isEditing
        ? await updateSpecialMarketCall(specialMarket.id, payload)
        : await createSpecialMarketCall(payload)
      showMessage("success", response?.data?.message || "Special market saved")
      await onSaved?.()
      setIsOpen(false)
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {isEditing ? `Edit Special Market — ${specialMarket.name}` : "Add Special Market"}
      </ModalHeader>
      <ModalBody>
        {isEditing && (
          <div className="d-flex gap-2 mb-3">
            <Button
              size="sm"
              color={view === "form" ? "primary" : "light"}
              onClick={() => setView("form")}
            >
              Details
            </Button>
            <Button
              size="sm"
              color={view === "leagues" ? "primary" : "light"}
              onClick={() => setView("leagues")}
            >
              Leagues ({leaguesCount})
            </Button>
          </div>
        )}

        {view === "form" && (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6} className="mb-3">
                <Label>Name</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Market Reference ID</Label>
                <Input
                  type="text"
                  value={form.market_reference_id}
                  onChange={e =>
                    setForm({ ...form, market_reference_id: e.target.value })
                  }
                  required
                />
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label>Calculation Type</Label>
                <Input
                  type="select"
                  value={form.calculation_type}
                  onChange={e =>
                    setForm({ ...form, calculation_type: e.target.value })
                  }
                  required
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </Input>
              </Col>
              <Col md={6} className="mb-3">
                <Label>Calculation Value</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.calculation_value}
                  onChange={e =>
                    setForm({ ...form, calculation_value: e.target.value })
                  }
                  required
                />
              </Col>
            </Row>

            <Row>
              <Col md={isEditing ? 6 : 12} className="mb-3">
                <Label>Settlement Condition</Label>
                <Input
                  type="text"
                  value={form.settlement_condition}
                  onChange={e =>
                    setForm({ ...form, settlement_condition: e.target.value })
                  }
                  required
                />
              </Col>
              {isEditing && (
                <Col md={6} className="mb-3">
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Input>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={12} className="mb-3">
                <Label>Description</Label>
                <Input
                  type="textarea"
                  value={form.description}
                  onChange={e =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button type="button" color="secondary" outline onClick={toggle}>
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        )}

        {view === "leagues" && isEditing && (
          <SpecialMarketLeaguesPanel
            specialMarket={specialMarket}
            onChanged={onSaved}
            onLeaguesCountChange={setLeaguesCount}
          />
        )}
      </ModalBody>
    </Modal>
  )
}
